import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { SnackbarService } from '@siemens/component-lib';
import { catchError, finalize, Observable, of, tap } from 'rxjs';
import { SignedUrl } from '@monorepo/interfaces';
import { QuarterlyExportService } from '../services/quarterly-export.service';
import { ClearQuarterlyExportDownloadLink, DownloadQuarterlyExport } from './quarterly-export.actions';

export interface QuarterlyExportStateModel {
    downloadLink?: string;
    isLoading: boolean;
    errorMessage?: string;
}

@State<QuarterlyExportStateModel>({
    name: 'quarterlyExport',
    defaults: {
        downloadLink: undefined,
        isLoading: false,
        errorMessage: undefined
    }
})
@Injectable()
export class QuarterlyExportState {
    private readonly quarterlyExportService = inject(QuarterlyExportService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly translateService = inject(TranslateService);

    @Selector()
    static downloadLink(state: QuarterlyExportStateModel): string | undefined {
        return state.downloadLink;
    }

    @Selector()
    static isLoading(state: QuarterlyExportStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static errorMessage(state: QuarterlyExportStateModel): string | undefined {
        return state.errorMessage;
    }

    @Action(DownloadQuarterlyExport)
    downloadQuarterlyExport(ctx: StateContext<QuarterlyExportStateModel>): Observable<SignedUrl | HttpErrorResponse> {
        ctx.patchState({ isLoading: true, errorMessage: undefined });

        return this.quarterlyExportService.getQuarterlyExportLink().pipe(
            tap(signedUrl => {
                ctx.patchState({
                    downloadLink: signedUrl.signedURL,
                    errorMessage: undefined
                });
                this.snackbarService.open({
                    title: this.translateService.instant('admin.quarterly-export.button'),
                    description: this.translateService.instant('admin.quarterly-export.success'),
                    type: 'success',
                    duration: 5000,
                    position: 'bottom-right'
                });
            }),
            catchError((error: HttpErrorResponse) => {
                ctx.patchState({
                    errorMessage: error.error?.message || 'An error occurred'
                });
                this.snackbarService.open({
                    title: this.translateService.instant('admin.quarterly-export.button'),
                    description: this.translateService.instant('admin.quarterly-export.error'),
                    type: 'error',
                    duration: 5000,
                    position: 'bottom-right'
                });
                return of(error);
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    @Action(ClearQuarterlyExportDownloadLink)
    clearQuarterlyExportDownloadLink(ctx: StateContext<QuarterlyExportStateModel>): void {
        ctx.patchState({
            downloadLink: undefined
        });
    }
}
